import {
  collection,
  addDoc,
  doc,
  onSnapshot,
  query,
  where,
  orderBy,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { firestore } from '../config/firebase';

const COLLECTION = 'fundWithdrawalRequests';

/**
 * Create a withdrawal request (investor requests recall of funds).
 * @param {{ fundInvestorId: string, lenderUid: string, investorUserId: string, amountRequested?: number | null, comment?: string | null, investingEntityType?: 'individual' | 'company', investingCompanyId?: string | null, investingDisplayName?: string | null }} data
 * @returns {Promise<string>} request doc id
 */
export async function createWithdrawalRequest(data) {
  const {
    fundInvestorId,
    lenderUid,
    investorUserId,
    amountRequested,
    comment,
    investingEntityType,
    investingCompanyId,
    investingDisplayName,
  } = data;
  if (!fundInvestorId || !lenderUid || !investorUserId) throw new Error('fundInvestorId, lenderUid and investorUserId required');
  const entityType = investingEntityType === 'company' ? 'company' : 'individual';
  const docRef = await addDoc(collection(firestore, COLLECTION), {
    fundInvestorId,
    lenderUid,
    investorUserId,
    amountRequested: amountRequested != null && !Number.isNaN(Number(amountRequested)) ? Number(amountRequested) : null,
    comment: (typeof comment === 'string' && comment.trim()) ? comment.trim() : null,
    investingEntityType: entityType,
    investingCompanyId: investingCompanyId && String(investingCompanyId).trim() ? String(investingCompanyId).trim() : null,
    investingDisplayName: investingDisplayName && String(investingDisplayName).trim() ? String(investingDisplayName).trim() : null,
    status: 'pending',
    requestedAt: serverTimestamp(),
    completedAt: null,
    completedByUid: null,
  });
  return docRef.id;
}

/**
 * Subscribe to withdrawal requests for a lender.
 * @param {string} lenderUid
 * @param {(items: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeWithdrawalRequestsByLender(lenderUid, callback) {
  if (!lenderUid) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('lenderUid', '==', lenderUid),
    orderBy('requestedAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => {
      const items = snap.docs.map((d) => {
        const data = d.data();
        return {
          id: d.id,
          ...data,
          requestedAt: data.requestedAt?.toDate?.() ?? data.requestedAt,
          completedAt: data.completedAt?.toDate?.() ?? data.completedAt,
        };
      });
      callback(items);
    },
    (err) => {
      console.error('[fundWithdrawalRequests] snapshot error (by lender):', err?.code, err?.message);
      callback([]);
    }
  );
}

/**
 * Subscribe to withdrawal requests for an investor (by their userId).
 * @param {string} investorUserId
 * @param {(items: Array<object>) => void} callback
 * @returns {() => void} unsubscribe
 */
export function subscribeWithdrawalRequestsByInvestor(investorUserId, callback) {
  if (!investorUserId) {
    callback([]);
    return () => {};
  }
  const q = query(
    collection(firestore, COLLECTION),
    where('investorUserId', '==', investorUserId),
    orderBy('requestedAt', 'desc')
  );
  return onSnapshot(q, (snap) => {
    const items = snap.docs.map((d) => {
      const data = d.data();
      return {
        id: d.id,
        ...data,
        requestedAt: data.requestedAt?.toDate?.() ?? data.requestedAt,
        completedAt: data.completedAt?.toDate?.() ?? data.completedAt,
      };
    });
    callback(items);
  });
}

/**
 * Mark a withdrawal request as completed or cancelled (lender only).
 * @param {string} requestId
 * @param {{ status: 'completed' | 'cancelled', completedByUid: string, repaidAt?: string }} data - repaidAt (YYYY-MM-DD) when status is 'completed'
 */
export async function updateWithdrawalRequest(requestId, data) {
  const ref = doc(firestore, COLLECTION, requestId);
  const payload = {
    status: data.status,
    completedAt: data.status !== 'pending' ? serverTimestamp() : null,
    completedByUid: data.completedByUid || null,
  };
  if (data.status === 'completed' && data.repaidAt) {
    payload.repaidAt = data.repaidAt;
  }
  await updateDoc(ref, payload);
}
